import React, { useState, useEffect } from 'react';
import { bool, oneOf } from 'prop-types';
import Popover, {
    positions,
    PopoverTrigger,
    PopoverLayover,
} from '@tamagoshi/core/Popover';
import { Outline as InfoOutlineIcon } from '@tamagoshi/icons/Info';
import classnames from 'classnames';
import { useIntl, defineMessages } from 'react-intl';
import useTooltipContent from './hooks/useTooltipContent';
import './Merchandizing.scss';

const messages = defineMessages({
    topSales: {
        id: 'listing.productCard.merchandizing.topSales',
        defaultMessage: 'Top Sales',
        description: 'Top sales label of product card merchandizing banner',
    },
    recommended: {
        id: 'listing.productCard.merchandizing.recommended',
        defaultMessage: 'Recommanded by Manomano',
        description: 'Recommanded label of product card merchandizing banner',
    },
    productOfTheWeek: {
        id: 'listing.productCard.merchandizing.productOfTheWeek',
        defaultMessage: 'Product of the week',
        description:
            'Product of the week label of product card merchandizing banner',
    },
    sponsored: {
        id: 'listing.productCard.merchandizing.sponsored',
        defaultMessage: 'Sponsored',
        description: 'Sponsored label of product card merchandizing banner',
    },
});

export default function Merchandizing({ type, withTooltip }) {
    const intl = useIntl();
    const [isClient, setIsClient] = useState(false);
    const [popOverVisbility, setPopOverVisibility] = useState(false);

    useEffect(() => {
        setIsClient(true);
    }, []);

    const onClose = () => setPopOverVisibility(false);
    const onClick = () => setPopOverVisibility(!popOverVisbility);
    const onEnter = event => {
        if (event.key === 'Enter') {
            setPopOverVisibility(!popOverVisbility);
        }
    };

    const trans = {
        topSales: intl.formatMessage(messages.topSales),
        recommended: intl.formatMessage(messages.recommended),
        productOfTheWeek: intl.formatMessage(messages.productOfTheWeek),
        sponsored: intl.formatMessage(messages.sponsored),
    };

    if (!trans[type]) {
        return null;
    }

    const merchandizingType = type === 'mmf' || type === 'b2b' ? '' : type;
    const rootClassnames = classnames(
        'product-card-merchandizing',
        'product-card-merchandizing--root',
        {
            [`product-card-merchandizing--${merchandizingType}`]: merchandizingType,
        },
    );
    const tooltipContent = useTooltipContent(type);

    const tooltipContentClassnames = classnames(
        'product-card-merchandizing-tooltip--content',
        {
            'product-card-merchandizing-tooltip--content-hidden': !popOverVisbility,
        },
    );

    return (
        <div className={rootClassnames}>
            <span>{trans[type]}</span>
            {withTooltip && tooltipContent && (
                <Popover
                    position={positions.BOTTOM}
                    fallbackPositions={positions.RIGHT}
                    isVisible={popOverVisbility}
                    onClose={onClose}
                >
                    <PopoverTrigger>
                        {({ ref }) => {
                            return (
                                <span
                                    role="button"
                                    tabIndex={0}
                                    ref={ref}
                                    className="product-card-merchandizing--info"
                                    onClick={onClick}
                                    onKeyPress={onEnter}
                                >
                                    <InfoOutlineIcon className="product-card--info-icon" />
                                </span>
                            );
                        }}
                    </PopoverTrigger>
                    <PopoverLayover>
                        {isClient && (
                            <div
                                className={tooltipContentClassnames}
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                    __html: tooltipContent,
                                }}
                            />
                        )}
                    </PopoverLayover>
                </Popover>
            )}
        </div>
    );
}

Merchandizing.propTypes = {
    type: oneOf([
        'topSales',
        'recommended',
        'productOfTheWeek',
        'mmf',
        'b2b',
        'sponsored',
    ]),
    withTooltip: bool,
};

Merchandizing.defaultProps = {
    type: 'topSales',
    withTooltip: false,
};
